import {
  CreateCustomFormAnswerDto,
  CreateCustomFormElementAnswerDto,
  CustomFormElementType,
  type CustomFormElementFullSizeDto,
  type CustomFormFullSizeDto,
  type CustomFormSectionResponseDto,
} from '@therame/shared';
import { useQuery } from '@tanstack/vue-query';
import type { AxiosInstance } from 'axios';
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';

const MANDATORY_FIELD_MESSAGE = 'Bitte alle mit * markierten Felder ausfüllen';

/**
 * What "empty" means depends on the element type: most types store their
 * answer as a plain string ('' = empty), but a checkbox stores '1'/'0' (so
 * '0' is its empty state) and the pain-location types store a JSON-encoded
 * array of picked points (so '[]' is theirs).
 */
/**
 * Where a half-answered questionnaire keeps its answers, keyed by form AND patient.
 *
 * Exported because the answers outlive every player: a parked draft is discarded from
 * a rail with nothing mounted, and clearing only the panel's own state would leave the
 * answers to reappear in the next one (TM-9455).
 */
export function customFormAnswersKey(formId: string, patientId: string): string {
  return `theracheck_answers_${formId}_${patientId}`;
}

/** The pre-patient key format. Only ever dropped, never read forward. */
export function legacyCustomFormAnswersKey(formId: string): string {
  return `theracheck_answers_${formId}`;
}

/**
 * Throw away one questionnaire's saved answers, with or without a player mounted.
 *
 * Storage may be unavailable (private browsing, quota); failing to clear is not worth
 * an exception on a discard path, and the in-memory copy is dropped by the caller
 * either way.
 */
export function clearCustomFormAnswers(formId: string, patientId: string): void {
  if (!formId || !patientId) return;
  try {
    localStorage.removeItem(customFormAnswersKey(formId, patientId));
    localStorage.removeItem(legacyCustomFormAnswersKey(formId));
  } catch {
    // See above.
  }
}

export function isAnswerEmpty(type: CustomFormElementType, value: string | undefined): boolean {
  if (value === undefined || value === '') return true;

  switch (type) {
    case CustomFormElementType.CHECKBOX:
      return value === '0';
    case CustomFormElementType.PAIN_LOCATION_3D:
    case CustomFormElementType.PAIN_LOCATION_3D_IMAGE:
      try {
        const parsed = JSON.parse(value);
        return !(Array.isArray(parsed) && parsed.length > 0);
      } catch {
        return true;
      }
    default:
      return false;
  }
}

export interface CustomFormPlayer {
  isLoading: ComputedRef<boolean>;
  isError: ComputedRef<boolean>;
  error: ComputedRef<unknown>;

  /** Sections, sorted by index. A form without section data behaves as one implicit section. */
  sortedCustomFormSections: ComputedRef<CustomFormSectionResponseDto[]>;
  /** Every element across all sections, in section-then-element order. */
  customFormElements: ComputedRef<CustomFormElementFullSizeDto[]>;

  currentSectionIndex: Ref<number>;
  currentSection: ComputedRef<CustomFormSectionResponseDto | undefined>;
  /** Elements of the section currently on screen, sorted by index. */
  currentSectionElements: ComputedRef<CustomFormElementFullSizeDto[]>;
  /** Jumps to the section that owns `elementId` (resolved once the form is loaded, if called earlier). */
  setCustomFormElementId: (elementId: string) => void;

  /** Answers captured so far this session, keyed by element id — not yet submitted. */
  answers: Ref<Map<string, string>>;
  /**
   * Records the values of the section currently on screen, in memory and in
   * localStorage, without moving off it.
   *
   * The card holds the visible section's values in per-element refs until the user
   * leaves the section, so a host that is about to be torn down — a page change
   * parking an unfinished questionnaire — needs a way to keep them.
   */
  recordCurrentAnswers: (currentAnswers?: Record<string, string>) => void;
  /** Discards any saved-but-not-yet-submitted answers, in memory and in localStorage. */
  clearAnswersFromStorage: () => void;

  /** Set when `moveNext`/`submit` was blocked by an empty mandatory field. */
  validationError: Ref<string | undefined>;
  /** The first empty mandatory element blocking navigation, so the caller can scroll it into view. */
  invalidElementId: Ref<string | undefined>;

  canMoveNext: ComputedRef<boolean>;
  canMovePrevious: ComputedRef<boolean>;
  /** `currentAnswers`: live values for every element of the current section, keyed by element id. */
  moveNext: (currentAnswers?: Record<string, string>) => void;
  movePrevious: (currentAnswers?: Record<string, string>) => void;
  /** Jumps directly to an earlier section (e.g. clicking it in the progress
   * tracker); a target at or after the current section is a no-op. */
  jumpToSection: (index: number, currentAnswers?: Record<string, string>) => void;
  /** True while a submit's POST is in flight; also true while `submit` rejects concurrent calls. */
  isSubmitting: Ref<boolean>;
  /** Resolves to false (without submitting) when blocked by an empty mandatory field. */
  submit: (currentAnswers?: Record<string, string>) => Promise<boolean>;
}

/**
 * Instance-scoped Player state: form id + patient id in, everything
 * the Player screens need out. Replaces the old global Pinia store so two
 * Players (patient page, therapist column) can run side by side without
 * overwriting each other's current step/answers — see `useCustomFormEditor`
 * for the same problem solved on the editor side.
 *
 * Loading is the one piece of server state, so it's the one piece behind
 * vue-query. The current step and the not-yet-submitted answers are session/
 * UI state, not server state — they live in plain refs instead, so they don't
 * fight the query cache's refetch/garbage-collection lifecycle.
 */
export function useCustomFormPlayer(
  formId: Ref<string | undefined>,
  patientId: Ref<string | undefined>,
  axiosInstance: AxiosInstance | undefined,
): CustomFormPlayer {
  const query = useQuery({
    queryKey: computed(() => ['custom-forms', formId.value] as const),
    queryFn: async () => {
      if (!axiosInstance) {
        throw new Error('useCustomFormPlayer: no axios instance provided.');
      }
      const response = await axiosInstance.get<CustomFormFullSizeDto>(
        `/custom-forms/${formId.value}`,
      );
      return response.data;
    },
    enabled: computed(() => !!formId.value && !!axiosInstance),
  });

  const form = computed(() => query.data.value);
  const isLoading = computed(() => query.isPending.value);
  const isError = computed(() => query.isError.value);
  const error = computed(() => query.error.value);

  // An element's `index` only counts within its own section, so the flat
  // list must be built by sorting sections first and then each section's
  // own elements.
  const sortedCustomFormSections = computed<CustomFormSectionResponseDto[]>(() => {
    return [...(form.value?.customFormSections ?? [])].sort((a, b) => a.index - b.index);
  });

  const customFormElements = computed<CustomFormElementFullSizeDto[]>(() => {
    if (sortedCustomFormSections.value.length > 0) {
      return sortedCustomFormSections.value.flatMap(
        (section) =>
          [...(section.customFormElements ?? [])].sort(
            (a, b) => a.index - b.index,
          ) as CustomFormElementFullSizeDto[],
      );
    }
    return form.value?.customFormElements ?? [];
  });

  const currentSectionIndex = ref(0);

  const currentSection = computed<CustomFormSectionResponseDto | undefined>(
    () => sortedCustomFormSections.value[currentSectionIndex.value],
  );

  // A form without section data (not yet migrated) is treated as one implicit
  // section holding every element, mirroring `customFormElements`'s own fallback.
  const currentSectionElements = computed<CustomFormElementFullSizeDto[]>(() => {
    if (sortedCustomFormSections.value.length === 0) return customFormElements.value;
    return [...(currentSection.value?.customFormElements ?? [])].sort(
      (a, b) => a.index - b.index,
    ) as CustomFormElementFullSizeDto[];
  });

  const totalSections = computed(() => Math.max(sortedCustomFormSections.value.length, 1));

  const resolveSectionForElement = (elementId: string): number =>
    sortedCustomFormSections.value.findIndex((section) =>
      (section.customFormElements ?? []).some((element) => element.id === elementId),
    );

  // If the form hasn't loaded yet, the id can't be resolved to a section
  // until the seed watcher below runs; it's retried there once data arrives.
  let pendingElementId: string | undefined;

  const setCustomFormElementId = (elementId: string) => {
    const sectionIndex = resolveSectionForElement(elementId);
    if (sectionIndex >= 0) {
      currentSectionIndex.value = sectionIndex;
    } else {
      pendingElementId = elementId;
    }
  };

  const answers = ref<Map<string, string>>(new Map());

  const getStorageKey = () => customFormAnswersKey(formId.value ?? '', patientId.value ?? '');

  // Old key format (form id only, no patient id) — the exact source of the
  // cross-patient leak this composable now prevents. Never migrate its value
  // forward; just drop it once so it stops lingering in localStorage.
  const legacyStorageKey = () => legacyCustomFormAnswersKey(formId.value ?? '');

  // Without a patient id, `getStorageKey()` would collapse every anonymous
  // session onto the same "...undefined" key, so storage access is skipped
  // entirely — answers still work, just in-memory for that session.
  const loadAnswersFromStorage = (): Record<string, string> => {
    if (!patientId.value) return {};
    try {
      const stored = localStorage.getItem(getStorageKey());
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };

  const clearAnswersFromStorage = () => {
    if (patientId.value) {
      try {
        localStorage.removeItem(getStorageKey());
      } catch {
        // Storage may be unavailable (private browsing, quota); in-memory clear below still applies.
      }
    }
    answers.value.clear();
  };

  // Seeds once the form arrives: restores any answers saved before a reload,
  // and resolves a caller-supplied element id (e.g. a resumed link) to its
  // section. If the same instance is later reused for a different
  // form/patient (e.g. one shared Player on a Praxis-Tablet), the previous
  // identity's state is dropped first so it can't leak into the new one.
  let loadedIdentity: string | undefined;

  const validationError = ref<string | undefined>();
  const invalidElementId = ref<string | undefined>();

  watch(
    [form, patientId],
    ([loadedForm]) => {
      if (!loadedForm) return;

      const identity = `${formId.value}:${patientId.value}`;
      if (loadedIdentity !== undefined && identity !== loadedIdentity) {
        answers.value.clear();
        currentSectionIndex.value = 0;
        validationError.value = undefined;
        invalidElementId.value = undefined;
      }
      loadedIdentity = identity;
      try {
        localStorage.removeItem(legacyStorageKey());
      } catch {
        // ignore — best-effort cleanup of a stale key
      }

      const stored = loadAnswersFromStorage();
      for (const [elementId, value] of Object.entries(stored)) {
        answers.value.set(elementId, value);
      }

      if (pendingElementId) {
        const sectionIndex = resolveSectionForElement(pendingElementId);
        if (sectionIndex >= 0) {
          currentSectionIndex.value = sectionIndex;
        }
        pendingElementId = undefined;
      }
    },
    // Sync so a consumer reading `currentSection` right after the query
    // resolves (same tick) sees the seeded state, not a stale undefined.
    { immediate: true, flush: 'sync' },
  );

  const canMoveNext = computed(() => currentSectionIndex.value < totalSections.value - 1);
  const canMovePrevious = computed(() => currentSectionIndex.value > 0);

  const findFirstMissingMandatoryElement = (
    currentAnswers?: Record<string, string>,
    elements: CustomFormElementFullSizeDto[] = currentSectionElements.value,
  ): CustomFormElementFullSizeDto | undefined =>
    elements.find((element) => {
      if (!element.mandatory) return false;
      const answer = currentAnswers?.[element.id] ?? answers.value.get(element.id);
      return isAnswerEmpty(element.type, answer);
    });

  const recordCurrentAnswers = (currentAnswers?: Record<string, string>) => {
    if (!currentAnswers) return;

    for (const [elementId, value] of Object.entries(currentAnswers)) {
      answers.value.set(elementId, value);
    }

    if (!patientId.value) return;

    try {
      const stored = loadAnswersFromStorage();
      Object.assign(stored, currentAnswers);
      localStorage.setItem(getStorageKey(), JSON.stringify(stored));
    } catch {
      // Storage may be unavailable (private browsing, quota); answers remain available in memory.
    }
  };

  const moveNext = (currentAnswers?: Record<string, string>) => {
    const missing = findFirstMissingMandatoryElement(currentAnswers);
    if (missing) {
      validationError.value = MANDATORY_FIELD_MESSAGE;
      invalidElementId.value = missing.id;
      return;
    }
    validationError.value = undefined;
    invalidElementId.value = undefined;
    recordCurrentAnswers(currentAnswers);
    if (!canMoveNext.value) return;
    currentSectionIndex.value += 1;
  };

  const movePrevious = (currentAnswers?: Record<string, string>) => {
    // Leaving backwards is never blocked by a mandatory field.
    validationError.value = undefined;
    invalidElementId.value = undefined;
    recordCurrentAnswers(currentAnswers);
    if (!canMovePrevious.value) return;
    currentSectionIndex.value -= 1;
  };

  /**
   * Jumps directly to an earlier section (e.g. clicking a past section in the
   * progress tracker) — same unblocked-by-mandatory-fields semantics as
   * `movePrevious`, just covering more than one step at once. A target at or
   * after the current section is a no-op: going backwards only skips the
   * PR4 gate by definition, so jumping forward would need the same
   * mandatory-field check `moveNext` already does, which this doesn't run.
   */
  const jumpToSection = (index: number, currentAnswers?: Record<string, string>) => {
    if (!Number.isInteger(index) || index < 0 || index >= currentSectionIndex.value) return;
    validationError.value = undefined;
    invalidElementId.value = undefined;
    recordCurrentAnswers(currentAnswers);
    currentSectionIndex.value = index;
  };

  const isSubmitting = ref(false);

  /** Returns false (without submitting) when a mandatory field in any section is empty. */
  const submit = async (currentAnswers?: Record<string, string>): Promise<boolean> => {
    if (isSubmitting.value) return false;

    // Recorded before the cross-section check so the current section's just-typed
    // answers aren't lost if the missing field turns out to live in another section.
    recordCurrentAnswers(currentAnswers);

    const missing = findFirstMissingMandatoryElement(undefined, customFormElements.value);
    if (missing) {
      validationError.value = MANDATORY_FIELD_MESSAGE;
      invalidElementId.value = missing.id;
      setCustomFormElementId(missing.id);
      return false;
    }
    validationError.value = undefined;
    invalidElementId.value = undefined;

    if (!axiosInstance) {
      throw new Error('useCustomFormPlayer: no axios instance provided.');
    }
    const userId = patientId.value;
    if (!userId) {
      throw new Error('User ID is required');
    }
    if (customFormElements.value.length === 0) {
      throw new Error('Form elements not found');
    }

    isSubmitting.value = true;
    try {
      const answerDto = new CreateCustomFormAnswerDto();
      answerDto.customFormId = formId.value;
      answerDto.userId = userId;

      for (const element of customFormElements.value) {
        const answerElement = new CreateCustomFormElementAnswerDto();
        const answer = answers.value.get(element.id);
        answerElement.answer = answer !== undefined && answer !== '' ? answer : undefined;
        answerElement.customFormElementId = element.id;
        answerDto.customFormElementAnswers.push(answerElement);
      }

      await axiosInstance.post(`/custom-forms/${formId.value}/answers`, answerDto, {
        timeout: 15000,
      });

      clearAnswersFromStorage();
      return true;
    } finally {
      isSubmitting.value = false;
    }
  };

  return {
    isLoading,
    isError,
    error,
    sortedCustomFormSections,
    customFormElements,
    currentSectionIndex,
    currentSection,
    currentSectionElements,
    setCustomFormElementId,
    answers,
    // Exposed so a host can commit the section ON SCREEN without moving off it. The
    // per-element values live in the card's own refs until the user leaves a section,
    // and a panel that is about to be torn down (a page change parking a draft) has to
    // be able to keep them — see CustomFormPlayer.commitVisibleAnswers.
    recordCurrentAnswers,
    clearAnswersFromStorage,
    validationError,
    invalidElementId,
    canMoveNext,
    canMovePrevious,
    moveNext,
    movePrevious,
    jumpToSection,
    isSubmitting,
    submit,
  };
}
